import {
  Column,
  Row,
  Square,
  Flex,
  SvgCaretRight,
  Stack,
  Heading,
  Copy,
  SvgExclamation,
} from '@grubhubprod/gh-cookbook-react';
import React from 'react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import {
  ILineOfCreditResponseType,
  LocTagInvite,
} from 'src/types/LineOfCredit';
import { getMealCreditBudgetString } from 'src/models/LineOfCreditModelUtils';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { RouterLink } from 'src/components/RouterLink/RouterLink';
import { MealCreditDurationsSummary } from 'src/components/oneCorp/features/MealCredit/components/DetailsPage/MealCreditDurationsSummary';
import { IMealCredit } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseMealCreditsDetailsPage.utils';

interface IEnterpriseMealCreditListCardProps {
  mealCredit: ILineOfCreditResponseType;
  isInactive?: boolean;
}

export function EnterpriseMealCreditListCard({
  mealCredit,
  isInactive,
}: IEnterpriseMealCreditListCardProps): JSX.Element {
  const { corpId } = useAuth();
  const { id: mealCreditId } = mealCredit;

  if (isInactive) {
    return (
      <EnterpriseMealCreditListCardContent
        mealCredit={mealCredit}
        isInactive={isInactive}
      />
    );
  }

  return (
    <RouterLink
      href={`/corporate/enterprise/${corpId}/meal-credits/${mealCreditId}/details`}
    >
      <EnterpriseMealCreditListCardContent
        mealCredit={mealCredit}
        isInactive={isInactive}
      />
    </RouterLink>
  );
}

function EnterpriseMealCreditListCardContent({
  mealCredit,
  isInactive,
}: IEnterpriseMealCreditListCardProps): JSX.Element {
  const { name, tag_invites } = mealCredit;
  const hasBudgets = get(tag_invites, 'length') > 0;

  function getMealCreditBudgetRange(budgets: LocTagInvite[]): string {
    if (budgets.length === 1) {
      const { budget } = budgets[0];

      return getMealCreditBudgetString(budget);
    }

    // spreading budgets into a new array so not to change the values in place
    const sortedBudgets = [...budgets].sort(
      ({ budget: budgetOne }, { budget: budgetTwo }) => budgetOne - budgetTwo
    );
    const minBudget = sortedBudgets[0];
    const maxBudget = sortedBudgets[sortedBudgets.length - 1];

    if (minBudget.budget === maxBudget.budget) {
      return getMealCreditBudgetString(maxBudget.budget);
    }

    const minBudgetString = getMealCreditBudgetString(minBudget.budget);
    const maxBudgetString = getMealCreditBudgetString(maxBudget.budget);

    return `${minBudgetString} - ${maxBudgetString}`;
  }

  return (
    <Square
      inset="lg"
      backgroundColor="untintedBackground"
      borderRadius="4px"
      dimension="1"
    >
      <Row gutter={24}>
        <Column gutter={24} phone={6}>
          <Flex flexDirection="column" height="100%" justifyContent="center">
            <Stack y="xxx-sm">
              <Heading
                variant="h6"
                color={isInactive ? 'secondaryText' : 'primaryText'}
              >
                {name}
              </Heading>
            </Stack>

            <MealCreditDurationsSummary
              mealCredit={mealCredit as IMealCredit}
            />
          </Flex>
        </Column>
        <Column gutter={24} phone={4}>
          <Flex alignItems="center" height="100%">
            {hasBudgets ? (
              <Copy variant="body">
                {getMealCreditBudgetRange(tag_invites)}/employee
              </Copy>
            ) : (
              <Flex
                alignItems="center"
                color="warning"
                data-testid="no-budgets-message"
              >
                {!isInactive && (
                  <Stack x="xx-sm">
                    <SvgExclamation />
                  </Stack>
                )}
                <Copy
                  variant="body"
                  color={isInactive ? 'primaryText' : 'warning'}
                >
                  {isInactive
                    ? 'No groups or amount assigned'
                    : 'Assign people or groups and set amount'}
                </Copy>
              </Flex>
            )}
          </Flex>
        </Column>
        {!isInactive && (
          <Column gutter={24} phone={2}>
            <Flex alignItems="center" justifyContent="flex-end" height="100%">
              <SvgCaretRight
                size="24px"
                aria-label="Go to meal credit details"
              />
            </Flex>
          </Column>
        )}
      </Row>
    </Square>
  );
}
